import React from 'react';
import { motion } from 'framer-motion';
import { useData } from '../../contexts/DataContext';
import { toast } from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiEdit2, FiTrash2, FiMapPin, FiWifi, FiClock, FiHome, FiImage } = FiIcons;

const PropertyList = ({ onEdit }) => {
  const { properties, deleteProperty, guestPages } = useData();

  const handleDelete = (property) => {
    if (window.confirm(`Are you sure you want to delete "${property.name}"? This will also delete all associated guest pages.`)) {
      deleteProperty(property.id);
      toast.success('Property deleted successfully!');
    }
  };

  const getActiveGuestCount = (propertyId) => {
    return guestPages.filter(page => page.propertyId === propertyId && page.status === 'active').length;
  };

  if (properties.length === 0) {
    return (
      <div className="text-center py-12">
        <SafeIcon icon={FiHome} className="text-gray-400 text-6xl mx-auto mb-4" />
        <h3 className="text-xl font-medium text-gray-900 mb-2">No Properties Yet</h3>
        <p className="text-gray-600">Add your first rental property to get started.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {properties.map((property) => (
        <motion.div
          key={property.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 overflow-hidden flex flex-col"
        >
          {/* Property Image */}
          <div className="h-48 w-full bg-gray-100 relative overflow-hidden group">
            {property.image ? (
              <img 
                src={property.image} 
                alt={property.name} 
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" 
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-300 bg-gray-50">
                <SafeIcon icon={FiImage} className="text-4xl" />
              </div>
            )}
            {/* Overlay Actions */}
            <div className="absolute top-2 right-2 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <button
                onClick={() => onEdit(property)}
                className="p-2 bg-white/90 hover:bg-white text-blue-600 rounded-lg shadow-sm backdrop-blur-sm transition-colors"
                title="Edit Property"
              >
                <SafeIcon icon={FiEdit2} className="text-sm" />
              </button>
              <button
                onClick={() => handleDelete(property)}
                className="p-2 bg-white/90 hover:bg-white text-red-600 rounded-lg shadow-sm backdrop-blur-sm transition-colors"
                title="Delete Property"
              >
                <SafeIcon icon={FiTrash2} className="text-sm" />
              </button>
            </div>
          </div>

          <div className="p-6 flex-1 flex flex-col">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-1 line-clamp-1">
                  {property.name}
                </h3>
                <div className="flex items-center text-sm text-gray-500 line-clamp-1">
                  <SafeIcon icon={FiMapPin} className="mr-1 flex-shrink-0" />
                  <span className="truncate">{property.address}</span>
                </div>
              </div>
            </div>

            <div className="space-y-3 text-sm text-gray-600 flex-1">
              {property.wifiName && (
                <div className="flex items-center">
                  <span className="w-5 flex justify-center mr-2"><SafeIcon icon={FiWifi} /></span>
                  <span className="truncate">{property.wifiName}</span>
                </div>
              )}
              <div className="flex items-center">
                <span className="w-5 flex justify-center mr-2"><SafeIcon icon={FiClock} /></span>
                <span>Check-in: {property.checkInTime}</span>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Active Guests</span>
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${getActiveGuestCount(property.id) > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                {getActiveGuestCount(property.id)} Stay{getActiveGuestCount(property.id) !== 1 && 's'}
              </span>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default PropertyList;